import {
    createStyles,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Paper,
    Tab,
    Tabs,
    Theme,
    Typography,
    withStyles,
    WithStyles,
    Button
} from "@material-ui/core";
import {
    KeyboardArrowUp,
    KeyboardArrowDown,
    AttachMoney
} from "@material-ui/icons";
import moment from "moment";
import "moment-timezone";
import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { IParcelDetail } from "../../../models/parcel/IParcelDetail";
import { IParcelTabIndicies } from "../../../models/parcel/IParcelTabIndicies";
import Constants from "../../../models/utility/Constants";
import { IAppDispatch, IAppState } from "../../../store";
import * as ParcelBidStore from "../../../store/ParcelBidStore";
import * as ParcelColumnCss from "../../../styles/ParcelColumnCss";
import CountdownTimer from "../timers/CountdownTimer";
import BidAmountText from "./BidAmountText";
import BidIncrementText from "./BidIncrementText";
import ParcelBidHistory from "./ParcelBidHistory";
import ParcelColumnHeaders from "./ParcelColumnHeaders";
import PlaceBidButton from "./PlaceBidButton";
import posthog from "posthog-js";

type ParcelPanelProps = ParcelBidStore.IParcelBidStoreState &
    typeof ParcelBidStore.actionCreators &
    WithStyles<typeof styles | typeof ParcelColumnCss.styles> & {
        auctionUrl: string;
        canPlaceBid: boolean;
        completed: boolean;
        isLive: boolean;
        parcel: IParcelDetail;
        isParcelClosed?: boolean;
        isPrivateSale?: boolean;
        hasAcceptedBids: boolean;
        salesPageUrl: string;
    };

interface IParcelPanelState {
    isExpanded: boolean;
    tabIndex: number;
    lastKnownTabIndex: number;
}
const WhiteTextTypography = withStyles({
    root: {
        color: "#FFFFFF"
    }
})(Typography) as typeof Typography;

const CustomButton = withStyles({
    contained: {
        color: "#000000"
    }
})(Button) as typeof Button;

const styles = (theme: Theme) =>
    createStyles({
        closedDate: {
            [theme.breakpoints.down("xs")]: {
                fontSize: 14,
                flexBasis: "30% !important"
            },
            fontSize: 18,
            fontWeight: "normal",
            color: "#F5F5F5",
            flexBasis: "27.5% !important"
        },
        liveTimer: {
            [theme.breakpoints.down("sm")]: {
                flexBasis: "60% !important"
            }
        },
        loaderWrapper: {
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            margin: theme.spacing(3),
            width: "100%"
        },
        panelArrow: {
            [theme.breakpoints.down("sm")]: {
                color: theme.palette.common.white,
                filter: "drop-shadow(3px 3px 3px black)",
                msFilter: "drop-shadow(3px 3px 3px black)",
                webkitFilter: "drop-shadow(3px 3px 3px black)"
            },
            color: theme.palette.text.primary,
            transform: "rotate(90deg)",
            transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms"
        },
        panelArrowExpanded: { transform: "rotate(270deg)" },
        panelArrowText: {
            [theme.breakpoints.down("sm")]: {
                color: theme.palette.common.white,
                filter: "drop-shadow(3px 3px 3px black)",
                msFilter: "drop-shadow(3px 3px 3px black)",
                webkitFilter: "drop-shadow(3px 3px 3px black)"
            },
            color: theme.palette.text.primary,
            fontSize: 13
        },
        panelParcelNumber: {
            fontSize: 20,
            fontWeight: "bold"
        },
        panelParcelName: {
            color: theme.palette.common.white,
            overflow: "hidden",
            textOverflow: "ellipsis",
            textShadow: "1px 2px 5px black"
        },
        panelParcelNameEnBloc: {
            color: theme.palette.common.white,
            overflow: "hidden",
            textOverflow: "ellipsis"
        },
        panelParcelNameSub: {
            [theme.breakpoints.down("xs")]: { fontSize: 12 },
            fontSize: 14,
            fontWeight: "normal",
            color: theme.palette.common.white
        },
        panelParcelNameWrapper: {
            flex: "1 1 auto",
            zIndex: 1,
            marginTop: theme.spacing(1),
            [theme.breakpoints.down("xs")]: { marginTop: 0 }
        },
        panelParcelLegalLandLocation: {
            marginTop: 2,
            fontSize: 14,
            fontWeight: "bold"
        },
        paper: {
            alignItems: "center",
            display: "flex",
            flexDirection: "column"
        },
        parcelImage: {
            [theme.breakpoints.down("sm")]: {
                borderBottomLeftRadius: 0,
                borderTopRightRadius: 10,
                bottom: "inherit"
            },
            backgroundColor: theme.palette.secondary.main,
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            borderBottomLeftRadius: 10,
            borderTopLeftRadius: 10,
            bottom: 0,
            cursor: "pointer",
            filter: "brightness(0.5)",
            opacity: 0.8,
            position: "absolute",
            top: 0
        },
        parcelImageDiv: {
            [theme.breakpoints.down("sm")]: {
                bottom: "inherit",
                justifyContent: "flex-start",
                paddingBottom: theme.spacing(2),
                paddingLeft: theme.spacing(3),
                paddingRight: theme.spacing(3),
                paddingTop: theme.spacing(2)
            },
            alignItems: "center",
            bottom: 0,
            cursor: "pointer",
            display: "flex",
            justifyContent: "center",
            paddingBottom: 6,
            paddingLeft: theme.spacing(4),
            paddingRight: theme.spacing(4),
            paddingTop: 6,
            position: "absolute",
            top: 0,
            transition: "padding 0.3s",
            alignSelf: "start"
        },
        parcelImageExpanded: {
            [theme.breakpoints.down("sm")]: {
                alignItems: "center",
                paddingBottom: theme.spacing(3),
                paddingTop: theme.spacing(3)
            },
            alignItems: "flex-end",
            paddingBottom: theme.spacing(5),
            paddingTop: theme.spacing(5),
            transition: "padding 0.3s"
        },
        parcelImageHeight: {
            [theme.breakpoints.down("sm")]: { height: 110, width: 1 }
        },
        parcelImageHeightExpanded: {
            [theme.breakpoints.down("sm")]: { height: 140, width: 1 }
        },
        enBlocBackground: {
            [theme.breakpoints.down("sm")]: {
                background: `linear-gradient(to bottom, ${theme.palette.primary.main} 10%, ${theme.palette.background.dark} 10%)`
            },
            background: `linear-gradient(to right, ${theme.palette.primary.main} 10%, ${theme.palette.background.dark} 10%)`
        },
        parcelMobileHeaders: {
            [theme.breakpoints.down("sm")]: {
                flexBasis: "100%",
                paddingLeft: theme.spacing(3)
            },
            flexBasis: "0%"
        },
        parcelPanel: {
            "&:before": { backgroundColor: "transparent !important" },
            borderRadius: "10px !important",
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(2),
            overflow: "hidden",
            minHeight: 130
        },
        parcelPanelExpanded: {
            marginBottom: `${theme.spacing(4)}px !important`,
            marginTop: theme.spacing(4)
        },
        parcelPanelSummary: {
            [theme.breakpoints.down("xs")]: {
                height: Constants.IsIE ? 295 : "auto"
            },
            [theme.breakpoints.down("sm")]: {
                "& > div:last-child": {
                    top: 50,
                    zIndex: 1
                },
                paddingLeft: 0,
                height: Constants.IsIE ? 305 : "auto"
            },
            "& > div": {
                flexWrap: "wrap",
                marginBottom: "0 !important",
                justifyContent: "end"
            },
            height: "auto",
            minHeight: 130,
            padding: 0,
            paddingLeft: "20%",
            backgroundColor: "#424242",
            visibility: "visible"
        },
        parcelPanelDetails: {
            [theme.breakpoints.down("sm")]: { paddingLeft: 0 },
            padding: 0,
            paddingLeft: "20%",
            width: "100%",
            backgroundColor: "#535353"
        },
        tab: {
            [theme.breakpoints.down("xs")]: { fontSize: 11 },
            backgroundColor: "#535353",
            marginLeft: 1,
            marginTop: 3,
            textAlign: "left",
            flexBasis: "100%",
            "& span": {
                display: "flex",
                alignItems: "center",
                width: "100%",
                flexDirection: "row"
            }
        },
        tabBar: {
            backgroundColor: "#424242",
            padding: 0,
            flexBasis: "100%",
            "& > div": {
                padding: 0,
                marginTop: 10
            }
        },
        tabBarNotLive: { width: "50%" },
        tabDetails: {
            [theme.breakpoints.down("xs")]: {
                height: Constants.IsIE ? 250 : "auto",
                minHeight: 250,
                paddingBottom: 0
            },
            alignItems: "flex-start",
            display: "flex",
            height: Constants.IsIE ? 315 : "auto",
            marginLeft: 1,
            boxShadow: "none"
        },
        tabNotSelected: {
            backgroundColor: "#747474",
            boxShadow: "inset 1px 1px 3px #00000029"
        },
        tabIcon: {
            color: theme.palette.common.white,
            fontSize: 28,
            marginRight: theme.spacing(),
            marginLeft: -theme.spacing()
        },
        tabPrimary: {
            fontSize: 14,
            lineHeight: 1.3,
            color: theme.palette.common.white
        },
        tabSub: {
            fontSize: 11,
            textTransform: "none",
            color: theme.palette.common.white,
            lineHeight: 1.3
        },
        offerToPurchaseLabel: {
            textAlign: "right",
            flexBasis: "78%",
            paddingRight: theme.spacing(5.5),
            color: "#C0C0C0",
            [theme.breakpoints.down("sm")]: {
                textAlign: "center",
                flexBasis: "100%",
                paddingRight: 0
            }
        },
        offerToPurchaseLink: {
            textDecoration: "underline",
            fontWeight: "bold",
            color: "rgba(220, 154, 29, 1)"
        },
        expandableTab: {
            display: "flex",
            justifyContent: "space-between",
            width: "100%"
        },
        collapseButton: {
            display: "flex"
        },
        collapseButtonText: {
            textTransform: "none",
            fontSize: "10px",
            color: theme.palette.common.white
        },
        hideMobile: {
            [theme.breakpoints.down("sm")]: {
                display: "none !important"
            }
        },
        ...ParcelColumnCss.styles(theme)
    });

const MAX_LEGAL_LAND_LOCATION_ROWS = 2;

class ParcelPanel extends React.PureComponent<
    ParcelPanelProps,
    IParcelPanelState
> {
    constructor(props: ParcelPanelProps) {
        super(props);

        if (props.isLive && !props.completed) {
            this.state = {
                isExpanded: true,
                tabIndex: 0,
                lastKnownTabIndex: 0
            };
        } else {
            this.state = {
                isExpanded: false,
                tabIndex: 1,
                lastKnownTabIndex: 1
            };
        }
    }

    public render() {
        const { classes, completed, isLive, parcel, parcelBidDict } =
            this.props;

        const isUpcoming = !isLive && !completed;

        const panelClasses = this.state.isExpanded
            ? `${classes.parcelPanel} ${classes.parcelPanelExpanded}`
            : classes.parcelPanel;
        const parcelImageClasses = this.state.isExpanded
            ? `${classes.parcelImage} ${classes.parcelImageExpanded}`
            : classes.parcelImage;
        const parcelImageDivClasses = this.state.isExpanded
            ? `${classes.parcelImageDiv} ${classes.parcelImageExpanded}`
            : classes.parcelImageDiv;
        const parcelImageHeightClasses = this.state.isExpanded
            ? classes.parcelImageHeightExpanded
            : classes.parcelImageHeight;

        const panelImageStyle = {
            backgroundImage: `url('${this.renderPanelImageUrl(parcel)}')`,
            visibility: "visible"
        } as React.CSSProperties;

        const parcelBids = parcelBidDict[parcel.parcelId] || [];

        const tabs = this.getTabs();

        const formatParcelLandLocations = (): string[] => {
            const legalLandDescriptions = parcel.parcelLocations.map(
                (landLocation) => landLocation.legalLandDescription
            );
            if (legalLandDescriptions.length <= MAX_LEGAL_LAND_LOCATION_ROWS) {
                return legalLandDescriptions;
            } else {
                // If there are more than 2 legal land locations, only show the first 1 and a count of the rest
                const numMoreLocations =
                    legalLandDescriptions.length -
                    (MAX_LEGAL_LAND_LOCATION_ROWS - 1);
                return [
                    ...legalLandDescriptions.slice(
                        0,
                        MAX_LEGAL_LAND_LOCATION_ROWS - 1
                    ),
                    `+${numMoreLocations} more locations`
                ];
            }
        };
        const formattedParcelLandLocations = formatParcelLandLocations();
        const parcelLandLocationsTitle = this.props.parcel.parcelLocations
            .map(
                (landLocation) =>
                    `${landLocation.legalLandDescription} (${landLocation.acres} ±Acres)`
            )
            .join(",\n");

        return (
            <Accordion
                key={parcel.parcelId}
                className={panelClasses}
                onChange={(e, expanded) =>
                    // Only allow parcels with bid log to be expanded
                    this.setState({
                        ...this.state,
                        isExpanded: !isUpcoming ? expanded : false
                    })
                }
                expanded={this.state.isExpanded}
            >
                <AccordionSummary className={classes.parcelPanelSummary}>
                    <div className={`${parcelImageHeightClasses}`} />
                    <div className={classes.parcelMobileHeaders}>
                        <ParcelColumnHeaders
                            completed={completed}
                            isLive={isLive}
                            showMobile={true}
                        />
                    </div>
                    <div
                        className={`${classes.columnFirst} ${
                            isLive ? classes.liveTimer : classes.closedDate
                        }`}
                    >
                        {isLive ? (
                            <CountdownTimer
                                entityId={parcel.parcelId}
                                date={parcel.auctionEndDate}
                                hideDayHourZeroes={true}
                                useCircleLoader={true}
                            />
                        ) : !completed ? (
                            <WhiteTextTypography component="h6" variant="h6">
                                {this.getDateText(parcel.auctionStartDate)}
                            </WhiteTextTypography>
                        ) : (
                            <WhiteTextTypography component="h6" variant="h6">
                                {this.getDateText(parcel.auctionEndDate)}
                            </WhiteTextTypography>
                        )}
                    </div>
                    {!isLive && !completed && (
                        <div className={classes.columnSecond}>
                            {this.getDateText(parcel.auctionEndDate)}
                        </div>
                    )}
                    <div
                        className={
                            isLive
                                ? classes.columnBidAmountLive
                                : !completed
                                ? classes.columnBidAmountNotLive
                                : classes.columnBidAmountCompleted
                        }
                    >
                        <BidAmountText
                            completed={this.props.completed}
                            isLive={this.props.isLive}
                            lastAcceptedBid={this.props.parcel.lastAcceptedBid}
                            parcelId={this.props.parcel.parcelId}
                            startingBid={this.props.parcel.startingBid}
                            isParcelClosed={this.props.isParcelClosed}
                            isPrivateSale={this.props.isPrivateSale}
                            hasAcceptedBids={this.props.hasAcceptedBids}
                            lastAcceptedBidOverride={
                                this.props.parcel.lastAcceptedBidOverride
                            }
                        />
                        {isUpcoming && (
                            <BidIncrementText
                                bidIncrement={parcel.bidIncrement}
                                text="Bid Increment"
                            />
                        )}
                    </div>
                    <div
                        className={`${classes.columnViewOfferToPurchaseCTA} ${
                            !isLive ? classes.columnLast : ""
                        }`}
                    >
                        <CustomButton
                            variant="contained"
                            color="primary"
                            size="small"
                            style={{ textAlign: "center" }}
                            href={Constants.Routes.ParcelDetails(
                                this.props.auctionUrl,
                                this.props.parcel.slug,
                                {
                                    baseUrl: this.props.salesPageUrl
                                }
                            ).toString()}
                        >
                            View Offer to Purchase & Details
                        </CustomButton>
                    </div>
                    {isLive && (
                        <div
                            className={`${classes.columnBidButtonLive} ${classes.columnLast}`}
                        >
                            <PlaceBidButton
                                bidIncrement={
                                    parcel.bidIncrement ||
                                    Constants.DefaultBidIncrement
                                }
                                canPlaceBid={this.props.canPlaceBid}
                                isLive={this.props.isLive}
                                parcelId={parcel.parcelId}
                                lastAcceptedBid={
                                    parcel.lastAcceptedBid ||
                                    parcel.startingBid ||
                                    0
                                }
                            />
                        </div>
                    )}
                    {tabs.numberOfTabs > 0 && (
                        <div className={classes.tabSection}>
                            <Tabs
                                className={classes.tabBar}
                                variant="fullWidth"
                                indicatorColor="primary"
                                textColor="primary"
                                value={this.state.tabIndex}
                                onChange={this.onPanelTabChange}
                            >
                                {tabs.visibility.live && (
                                    <Tab
                                        value={tabs.indicies.live}
                                        className={`${classes.tab} ${
                                            this.state.tabIndex !==
                                                tabs.indicies.live &&
                                            this.state.isExpanded
                                                ? classes.tabNotSelected
                                                : ""
                                        }`}
                                        label={
                                            <React.Fragment>
                                                <AttachMoney
                                                    fontSize="small"
                                                    className={classes.tabIcon}
                                                />
                                                <div
                                                    className={
                                                        classes.expandableTab
                                                    }
                                                >
                                                    <div>
                                                        <Typography
                                                            className={
                                                                classes.tabPrimary
                                                            }
                                                            variant="h6"
                                                            color="textPrimary"
                                                        >
                                                            Auction History
                                                        </Typography>
                                                        <Typography
                                                            className={
                                                                classes.tabSub
                                                            }
                                                            variant="subtitle1"
                                                        >
                                                            {parcelBids.length ===
                                                            1
                                                                ? "1 Bid"
                                                                : parcelBids.length +
                                                                  " Bids"}
                                                        </Typography>
                                                    </div>
                                                    {this.renderExpandAndCollapseToggle(
                                                        {
                                                            tab: tabs.indicies
                                                                .live
                                                        }
                                                    )}
                                                </div>
                                            </React.Fragment>
                                        }
                                    />
                                )}
                            </Tabs>
                        </div>
                    )}
                </AccordionSummary>
                <div
                    className={`${parcelImageClasses} ${
                        classes.columnParcelImageWidth
                    } ${parcelImageHeightClasses} ${
                        parcel.enBloc ? classes.enBlocBackground : ""
                    }`}
                    style={!parcel.enBloc ? panelImageStyle : {}}
                />
                <div
                    className={`${parcelImageDivClasses} ${classes.columnParcelImageWidth} ${parcelImageHeightClasses}`}
                    id={`${parcel.parcelId}`}
                    // MUI's Accordion tries to collapse everything after the first element in it so this prevents the image from being hidden
                    style={{ visibility: "visible" }}
                >
                    <div
                        className={`${classes.panelParcelNameWrapper} ${classes.columnParcelImageWidth}`}
                    >
                        <Typography
                            className={`${
                                parcel.enBloc
                                    ? classes.panelParcelNameEnBloc
                                    : classes.panelParcelName
                            } ${classes.panelParcelNumber}`}
                            component="h4"
                            variant="h4"
                            noWrap={true}
                        >
                            {parcel.name}
                        </Typography>
                        {formattedParcelLandLocations.map(
                            (parcelLegalLandLocation) => (
                                <Typography
                                    key={parcelLegalLandLocation}
                                    className={`${classes.panelParcelName} ${classes.panelParcelLegalLandLocation}`}
                                    component="h5"
                                    variant="h5"
                                    noWrap={true}
                                    title={parcelLandLocationsTitle}
                                >
                                    {parcelLegalLandLocation}
                                </Typography>
                            )
                        )}
                    </div>
                </div>
                <AccordionDetails className={classes.parcelPanelDetails}>
                    {tabs.numberOfTabs > 0 && (
                        <div className={classes.tabSectionOffset} />
                    )}
                    {tabs.numberOfTabs > 0 && (
                        <div className={classes.tabSection}>
                            <Paper
                                className={classes.tabDetails}
                                square={true}
                                elevation={2}
                            >
                                {this.state.tabIndex === tabs.indicies.live &&
                                    this.state.isExpanded && (
                                        <ParcelBidHistory
                                            bidAmount={
                                                parcel.lastAcceptedBid ||
                                                parcel.startingBid ||
                                                0
                                            }
                                            bidIncrement={
                                                parcel.bidIncrement ||
                                                Constants.DefaultBidIncrement
                                            }
                                            canPlaceBid={this.props.canPlaceBid}
                                            parcelId={parcel.parcelId}
                                        />
                                    )}
                            </Paper>
                        </div>
                    )}
                </AccordionDetails>
            </Accordion>
        );
    }

    private getTabs = () => {
        const { parcel, isLive } = this.props;
        const visibility = {
            photos: !parcel.enBloc && this.doesParcelHavePhotos(parcel),
            live: isLive,
            documents: null
        };

        const indicies = {
            live: 0
        } as IParcelTabIndicies;

        let numberOfTabs = 0;

        if (visibility.live) {
            numberOfTabs++;
        }
        if (visibility.photos) {
            numberOfTabs++;
        }
        if (visibility.documents) {
            numberOfTabs++;
        }

        return { visibility, indicies, numberOfTabs };
    };

    private onPanelTabChange = (
        _event: React.ChangeEvent<{}>,
        value: number
    ) => {
        if (this.state.tabIndex === value && this.state.isExpanded) {
            this.setState({
                isExpanded: false,
                tabIndex: 0,
                lastKnownTabIndex: value
            });
            posthog.capture("ParcelPanel.TabCollapsed", {
                $set: {
                    parcel: this.props.parcel.name,
                    tabIndex: this.state.tabIndex
                }
            });
            return;
        }

        this.setState({
            isExpanded: true,
            tabIndex: value,
            lastKnownTabIndex: value
        });
        posthog.capture("ParcelPanel.TabChanged", {
            $set: { parcel: this.props.parcel.name }
        });
    };

    private getDateText = (date: string) => {
        const classes = this.props.classes;
        const formatedDate = moment(date)
            .tz(Constants.TimeZone)
            .format(Constants.DateFormat);
        return (
            <div>
                <WhiteTextTypography
                    className={classes.closedDate}
                    component="h6"
                    variant="h6"
                >
                    {formatedDate.split("at")[0]}
                </WhiteTextTypography>
                <WhiteTextTypography
                    className={classes.closedDate}
                    component="h6"
                    variant="h6"
                >
                    {formatedDate.split("at")[1]}
                </WhiteTextTypography>
            </div>
        );
    };

    private getParcelPhotosToShow = (parcel: IParcelDetail) => {
        return {
            hasLatLong: !!parcel.latitude && !!parcel.longitude,
            hasPictures:
                !!parcel.pictureMetadata && parcel.pictureMetadata.length > 0
        };
    };

    private doesParcelHavePhotos = (parcel: IParcelDetail) => {
        const parcelDetailsToShow = this.getParcelPhotosToShow(parcel);
        return (
            parcelDetailsToShow.hasPictures || parcelDetailsToShow.hasLatLong
        );
    };

    private renderPanelImageUrl = (parcel: IParcelDetail) => {
        if (!parcel.pictureMetadata || parcel.pictureMetadata.length === 0) {
            return "";
        }

        const img = parcel.pictureMetadata[0];
        return img.url;
    };

    private renderExpandAndCollapseToggle = (options: { tab: number }) => {
        const classes = this.props.classes;

        const selected = this.state.lastKnownTabIndex === options.tab;
        if (!selected) {
            return;
        }

        return (
            <div className={classes.collapseButton}>
                <Typography
                    className={classes.collapseButtonText}
                    variant="caption"
                >
                    <span className={classes.hideMobile}>
                        {this.state.isExpanded ? "Collapse" : "Expand"}
                    </span>
                    {this.state.isExpanded ? (
                        <KeyboardArrowUp fontSize="small" />
                    ) : (
                        <KeyboardArrowDown fontSize="small" />
                    )}
                </Typography>
            </div>
        );
    };
}

const reduxState = (state: IAppState) =>
    Object.assign({}, state.parcelBidStoreState);
const reduxDispatch = (dispatch: IAppDispatch) =>
    bindActionCreators(
        Object.assign({}, ParcelBidStore.actionCreators),
        dispatch
    );

export default withStyles(styles)(
    connect(reduxState, reduxDispatch)(ParcelPanel)
);
